<script>
import { mapActions, mapGetters } from 'vuex'
import baseMixin from '@/mixins/baseMixin'
import { DisputePerspective } from '@/constants/disputes/DisputePerspective'
import SideDrawer from '@/components/general/side-drawer-dialog.vue'
import Spinner from '@/components/general/spinner.vue'
import { isNullOrEmptyArray } from '@/helpers/array-helpers'
import { ShortTermStatus } from '@/constants/booking/ShortTermStatus'
import DisputesDialogContent from './disputes-dialog/disputes-dialog-content.vue'
import InvoiceCenterDialogContent from './disputes-dialog/invoice-bulk-action-dialog-content.vue'
import DisputesTaskDialogContent from './disputes-dialog/disputes-dialog-task-form.vue'
import DisputesTableOverviewStatsBreakdownCard from './disputes-table-overview-stats-breakdown-card.vue'
import DisputeTableInvoiceCenterStatsBreakdownCard from './dispute-table-invoice-center-stats-breakdown-card.vue'
import DisputeResolutionConfirmationDialog from './dispute-resolution-confirmation-dialog.vue'
import DisputesTableV2 from './disputes-table-v2.vue'
import DisputesTaskTable from './disputes-task-table.vue'

export default {
  name: 'BookingDisputeListV2',
  components: {
    SideDrawer,
    DisputesDialogContent,
    InvoiceCenterDialogContent,
    DisputesTaskDialogContent,
    Spinner,
    DisputesTableV2,
    DisputesTaskTable,
    DisputesTableOverviewStatsBreakdownCard,
    DisputeTableInvoiceCenterStatsBreakdownCard,
    DisputeResolutionConfirmationDialog,
  },
  mixins: [baseMixin],
  data() {
    return {
      timesheetSelectTypes: ['All', 'Missing', 'Conflict'],
      filterModel: null,
      disputePerspective: DisputePerspective,
      isFlyoutLoading: false,
      isGraphLoading: false,
      invoiceCenterId: '',
      invoiceCenterDateUntil: null,
      graphLabels: ['Pending', 'Candidate', 'Client'],
      graphData: [],
      tableOverviewDataPoints: [],
      disputeCategoryFilterKeys: [],
      tab: 0,
    }
  },
  computed: {
    ...mapGetters({
      isLoadingBookingDisputes: 'disputes/isLoading',
      allowedToViewAdminIds: 'disputes/getAllowedToViewAdminIds',
      currentUser: 'auth/currentUser',
      activeTimesheet: 'disputes/activeTimesheet',
      resolveStatus: 'disputes/resolveStatus',
      showFlyout: 'disputes/showFlyout',
      showResolveContent: 'disputes/showResolveContent',
      showDisputesTaskContent: 'disputes/showDisputesTaskContent',
      showInvoiceCenterContent: 'disputes/showInvoiceCenterContent',
      getInvoiceCenterGroupedData: 'disputes/getInvoiceCenterGroupedData',
      isFinanceUser: 'auth/isFinanceUser',
      invoiceCenter: 'disputes/invoiceCenter',
      isLoadingDisputeSubmit: 'disputes/isLoadingDisputeSubmit',
      disputeTasks: 'disputes/disputeFollowUpTasks',
    }),
    autocompleteSelectedModel: {
      get() {
        return this.filterModel
      },
      set(val) {
        this.filterModel = val
      },
    },
    invoiceCenterData() {
      return this.invoiceCenter(this.invoiceCenterId)
    },
    isLoading() {
      return this.isFlyoutLoading || this.isLoadingDisputeSubmit
    },
    disputeBookingStatusFilter() {
      if (isNullOrEmptyArray(this.disputeCategoryFilterKeys)) return []

      const bookingStatuses = []

      for (const filterKey of this.disputeCategoryFilterKeys) {
        switch (filterKey) {
          case 'dispute_stats_not_submitted':
            bookingStatuses.push(ShortTermStatus.BOOKING_NOT_APPROVED)
            bookingStatuses.push(ShortTermStatus.ECH_BOOKING_NOT_APPROVED)
            break
          case 'dispute_stats_modified':
            bookingStatuses.push(
              ShortTermStatus.CANDIDATE_SUBMITTED_TIMESHEET_MODIFIED
            )
            bookingStatuses.push(
              ShortTermStatus.CANDIDATE_SUBMITTED_TIMESHEET_DID_NOT_HAPPEN
            )
            bookingStatuses.push(
              ShortTermStatus.CLIENT_SUBMITTED_TIMESHEET_MODIFIED
            )
            bookingStatuses.push(
              ShortTermStatus.CLIENT_SUBMITTED_TIMESHEET_DID_NOT_HAPPEN
            )
            bookingStatuses.push(ShortTermStatus.TIMESHEET_DISPUTE_OR_MISMATCH)
            break
          case 'dispute_stats_other':
            bookingStatuses.push(
              ShortTermStatus.CANDIDATE_SUBMITTED_TIMESHEET_NO_CHANGES
            )
            bookingStatuses.push(
              ShortTermStatus.CLIENT_SUBMITTED_TIMESHEET_NO_CHANGES
            )
            break
        }
      }

      return bookingStatuses
    },
    tabs() {
      return [
        {
          key: 'disputes',
          title: this.$t('dispute.tabs.disputes'),
          count: this.graphData?.length >= 100 ? '99+' : this.graphData?.length,
        },
        {
          key: 'tasks',
          title: this.$t('dispute.tabs.tasks'),
          count:
            this.disputeTasks?.length >= 100
              ? '99+'
              : this.disputeTasks?.length,
        },
      ]
    },
  },
  watch: {
    autocompleteSelectedModel: {
      async handler() {
        await this.loadTaskList()
      },
      deep: true,
    },

    tabs: {
      handler() {
        this.$refs.tabs.callSlider() // redraw tab underline on count change
      },
    },
  },
  async created() {
    if (!isNullOrEmptyArray(this.autocompleteSelectedModel?.ids))
      await this.loadTaskList()
  },
  methods: {
    ...mapActions({
      submitTimesheets: 'disputes/submitTimesheets',
      getDisputeDetailsById: 'disputes/getDisputeDetailsById',
      getDisputeFollowUpTasks: 'disputes/getDisputeFollowUpTasks',
    }),
    async loadTaskList() {
      this.$store.commit('disputes/START_LOADING_TASK_TABLE')
      await this.getDisputeFollowUpTasks({
        params: {
          filter: this.autocompleteSelectedModel,
        },
      })

      this.$store.commit('disputes/FINISH_LOADING_TASK_TABLE')
    },
    updateFilter(filterModel) {
      this.autocompleteSelectedModel = filterModel
    },
    setGraphData(data) {
      this.graphData = data
    },
    async loadList(refresh) {
      await this.$refs.disputesTable.loadList(refresh)
      await this.loadTaskList()
    },
    async loadBookingDetails(item) {
      this.isFlyoutLoading = true
      this.$store.commit('disputes/SET_FLYOUT', { key: 'show', value: true })

      if (item?.taskId) {
        await this.openTaskForm(item)
        this.isFlyoutLoading = false
        return
      }

      const { data } = await this.getDisputeDetailsById(item.bookingId)
      this.$store.commit('disputes/SET_FLYOUT', {
        key: 'timesheet',
        value: data,
      })

      this.isFlyoutLoading = false
    },
    async openTaskForm(item) {
      this.$store.commit('disputes/SET_FLYOUT', {
        key: 'task',
        value: { bookingId: item.bookingId, id: item.taskId },
      })
      await this.$store.dispatch('disputes/getTaskDetails')
    },
    openInvoiceCenterFlyout({ invoiceCenterId, date }) {
      this.isFlyoutLoading = true
      this.invoiceCenterId = invoiceCenterId
      this.invoiceCenterDateUntil = date

      this.$store.commit('disputes/SET_FLYOUT', { key: 'show', value: true })

      this.$store.commit('disputes/SET_FLYOUT', {
        key: 'invoiceCenter',
        value: this.invoiceCenterData,
      })

      this.$store.commit('disputes/SET_FLYOUT', {
        key: 'invoiceCenterDateUntil',
        value: this.invoiceCenterDateUntil,
      })

      this.isFlyoutLoading = false
    },
    closeFlyout() {
      this.$store.commit('disputes/RESET_FLYOUT')
    },
    async submitInvoiceCenterBulkAction() {
      this.isFlyoutLoading = true
      await this.bulkResolveByInvoiceCenter()
      this.isFlyoutLoading = false
      this.closeFlyout()
      await this.loadList(true)
    },
    handleFilterChange(payload) {
      this.disputeCategoryFilterKeys = payload
    },
  },
}
</script>

<template>
  <div class="mt-4">
    <v-row fill-height>
      <v-col cols="12" lg="6" fill-height>
        <DisputesTableOverviewStatsBreakdownCard
          :disputes="graphData"
          @filterUpdated="updateFilter"
          @filterChanged="handleFilterChange"
        />
      </v-col>
      <v-col v-if="isFinanceUser" cols="12" lg="6">
        <DisputeTableInvoiceCenterStatsBreakdownCard
          @openInvoiceCenterFlyout="openInvoiceCenterFlyout"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-tabs ref="tabs" v-model="tab" background-color="transparent">
          <v-tab v-for="{ title, key, count } in tabs" :key="key">
            {{ title }}
            {{ count ? `(${count})` : '' }}
          </v-tab>
        </v-tabs>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-tabs-items v-model="tab">
          <v-tab-item>
            <DisputesTableV2
              ref="disputesTable"
              :autocomplete-selected-model="autocompleteSelectedModel"
              :status-filters="disputeBookingStatusFilter"
              @loadBookingDetails="loadBookingDetails"
              @setGraphData="setGraphData"
            />
          </v-tab-item>
          <v-tab-item>
            <DisputesTaskTable
              ref="taskTable"
              :autocomplete-selected-model="autocompleteSelectedModel"
              @openTask="loadBookingDetails"
            />
          </v-tab-item>
        </v-tabs-items>
      </v-col>
    </v-row>
    <SideDrawer
      :show="showFlyout"
      :xl="5"
      :lg="6"
      :md="10"
      :title="$t('dispute.resolutionDialog.title')"
      @closeDialog="closeFlyout"
    >
      <DisputesDialogContent v-if="showResolveContent && !isLoading" />
      <InvoiceCenterDialogContent
        v-if="showInvoiceCenterContent && !isLoading"
      />
      <DisputesTaskDialogContent
        v-if="showDisputesTaskContent && !isLoading"
        @refreshTaskList="loadTaskList"
      />
      <Spinner v-if="isLoading" class="mt-10" size="60" />
      <template v-slot:actions="{ close }">
        <v-spacer />
        <v-btn
          data-testid="side-drawer-dialog-close-btn"
          :large="!isMediumOrLower"
          depressed
          :disabled="isLoading"
          :color="`grey ${isDarkMode ? 'darken-5' : 'lighten-4'}`"
          class="px-8"
          @click="close"
        >
          {{ $t('general.cancel') }}
        </v-btn>
      </template>
    </SideDrawer>
    <DisputeResolutionConfirmationDialog @complete="loadList(true)" />
  </div>
</template>
